/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { MouseEventHandler, forwardRef } from "react";

import AmplitudeEvent from "types/enums/AmplitudeEvent";
import ButtonName from "types/enums/ButtonName";
import ButtonTheme from "types/enums/ButtonTheme";
import FontClass from "types/enums/FontClass";
import GlobalClass from "types/enums/GlobalClass";
import { Link } from "react-router-dom";
import joinClasses from "utils/joinClasses";
import styles from "css/buttons/ButtonWithText.module.css";
import useLogEvent from "hooks/useLogEvent";
import LoadingSpinner from "components/loading/LoadingSpinner";
import ColorValue from "types/enums/ColorValue";

type Props = {
  buttonName?: ButtonName;
  buttonTheme: ButtonTheme;
  children: string | JSX.Element | Array<string | JSX.Element>;
  className?: string;
  disabled?: boolean;
  fontClass: FontClass;
  height?: number;
  href?: string;
  icon?: JSX.Element;
  iconPosition?: "left" | "right";
  logProperties?: { [key: string]: any };
  onClick?: MouseEventHandler;
  showLoadingSpinner?: boolean;
  style?: { [key: string]: string | number };
  target?: string;
  textTransform?: "none" | "uppercase";
  type?: "button" | "link_external" | "link_internal" | "submit";
  width?: "auto" | "100%";
};

// TODO: Not everything is right
const BUTTON_THEME_TO_SPINNER_COLOR = {
  [ButtonTheme.BrightPurpleOutline]: ColorValue.White,
  [ButtonTheme.LightPinkOutline]: ColorValue.White,
  [ButtonTheme.Navy]: ColorValue.White,
  [ButtonTheme.NavyOutlineSquare]: ColorValue.White,
  [ButtonTheme.PurpleGradient]: ColorValue.White,
  [ButtonTheme.PurpleOutline]: ColorValue.White,
  [ButtonTheme.Red]: ColorValue.White,
  [ButtonTheme.RedOutline]: ColorValue.White,
  [ButtonTheme.SecondaryOutline]: ColorValue.White,
};

function getClassNameForButtonTheme(
  buttonTheme: ButtonTheme,
  isLink: boolean,
  disabled: boolean
): string {
  switch (buttonTheme) {
    case ButtonTheme.BrightPurpleOutline:
      return joinClasses(
        isLink
          ? styles.brightPurpleOutlineThemeLink
          : styles.brightPurpleOutlineTheme,
        disabled ? styles.disabled : null
      );
    case ButtonTheme.LightPinkOutline:
      return joinClasses(
        isLink
          ? styles.lightPinkOutlineThemeLink
          : styles.lightPinkOutlineTheme,
        disabled ? styles.disabled : null
      );
    case ButtonTheme.Navy:
      return joinClasses(
        isLink ? styles.navyThemeLink : styles.navyTheme,
        disabled ? styles.disabled : null
      );
    case ButtonTheme.NavyOutlineSquare:
      return joinClasses(
        isLink
          ? styles.navyOutlineSquareThemeLink
          : styles.navyOutlineSquareTheme,
        disabled ? styles.disabled : null
      );
    case ButtonTheme.PurpleGradient:
      return joinClasses(
        isLink ? styles.purpleGradientThemeLink : styles.purpleGradientTheme,
        disabled ? styles.disabled : null
      );
    case ButtonTheme.PurpleOutline:
      return joinClasses(
        isLink ? styles.purpleOutlineThemeLink : styles.purpleOutlineTheme,
        disabled ? styles.disabled : null
      );
    case ButtonTheme.Red:
      return joinClasses(
        isLink ? styles.redThemeLink : styles.redTheme,
        disabled ? styles.disabled : null
      );
    case ButtonTheme.RedOutline:
      return joinClasses(
        isLink ? styles.redOutlineThemeLink : styles.redOutlineTheme,
        disabled ? styles.disabled : null
      );
    case ButtonTheme.SecondaryOutline:
      return joinClasses(
        isLink
          ? styles.secondaryOutlineThemeLink
          : styles.secondaryOutlineTheme,
        disabled ? styles.disabled : null
      );
    default:
      break;
  }

  throw new Error(`unsupported buttonTheme ${buttonTheme}`);
}

// forwardRef required to make this work with NextJS Link, see
// https://nextjs.org/docs/api-reference/next/link#if-the-child-is-a-function-component.
const ButtonWithText = forwardRef<HTMLButtonElement, Props>(
  (
    {
      buttonName,
      buttonTheme,
      children,
      className,
      disabled = false,
      fontClass,
      height,
      href,
      icon,
      iconPosition = "right",
      logProperties,
      onClick,
      showLoadingSpinner = false,
      style = {},
      target = "_blank",
      textTransform,
      type = "button",
      width = "auto",
    }: Props,
    ref
  ) => {
    const logEvent = useLogEvent();
    const styleToUse = {
      ...style,
      height,
      ...(textTransform == null ? {} : { textTransform }),
    };
    const classNameJoined = joinClasses(
      getClassNameForButtonTheme(
        buttonTheme,
        type === "link_internal" || type === "link_external",
        disabled
      ),
      styles.button,
      width === "auto" ? styles.buttonAutoWidth : styles.button100Width,
      fontClass,
      className
    );
    // eslint-disable-next-line no-param-reassign
    children = showLoadingSpinner ? (
      <LoadingSpinner
        // @ts-ignore should clean this up later...
        colorValue={BUTTON_THEME_TO_SPINNER_COLOR[buttonTheme]}
        // @ts-ignore should clean this up later...
        fontClass={fontClass}
      />
    ) : (
      children
    );

    const childrenWithIcon = (
      <>
        {icon && iconPosition === "left" && (
          <div
            className={joinClasses(
              styles.icon,
              styles.iconLeft,
              GlobalClass.HideText
            )}
          >
            {icon}
          </div>
        )}
        {children}
        {icon && iconPosition === "right" && (
          <div
            className={joinClasses(
              styles.icon,
              styles.iconRight,
              GlobalClass.HideText
            )}
          >
            {icon}
          </div>
        )}
      </>
    );
    const onClickWithLog: MouseEventHandler = (e) => {
      if (buttonName != null) {
        logEvent(AmplitudeEvent.ButtonClick, { buttonName, ...logProperties });
      }
      if (onClick != null) {
        onClick(e);
      }
    };

    if (type === "link_internal") {
      return (
        <Link className={styles.linkInternal} to={href ?? ""}>
          <div
            className={joinClasses(classNameJoined, styles.linkContent)}
            onClick={onClickWithLog}
            style={styleToUse}
          >
            {childrenWithIcon}
          </div>
        </Link>
      );
    }

    if (type === "link_external") {
      return (
        <a
          className={joinClasses(classNameJoined, styles.linkContent)}
          href={href ?? ""}
          onClick={onClickWithLog}
          style={styleToUse}
          target={target}
          rel={target === "_blank" ? "noreferrer" : undefined}
        >
          {childrenWithIcon}
        </a>
      );
    }

    return (
      <button
        ref={ref}
        className={classNameJoined}
        disabled={disabled}
        onClick={onClickWithLog}
        style={styleToUse}
        // eslint-disable-next-line react/button-has-type
        type={type}
      >
        {childrenWithIcon}
      </button>
    );
  }
);

export default ButtonWithText;
